import * as ex from 'excalibur';
import { TileType } from '../types/enums';
import { GridPosition, TileConfig } from '../types/interfaces';
import { TILE_SIZE, GRID_WIDTH, GRID_HEIGHT, TILE_PROPERTIES } from '../config/constants';
import { Entity } from './entity';
import { GridPositionPool } from './objectPool';
// Import the Game type but define it as any to avoid circular dependencies
type Game = any;

export class Grid {
  public game: Game;
  private tiles: TileType[][];
  private entities: (Entity | null)[][];
  private tileActors: ex.Actor[][] = [];
  
  // Cached vector for position calculations
  private cachedVector = new ex.Vector(0, 0);
  
  // Pool of grid positions for reuse
  private positionPool: GridPositionPool = GridPositionPool.getInstance();

  constructor(game: Game) {
    this.game = game;
    this.tiles = Array(GRID_HEIGHT).fill(0).map(() => Array(GRID_WIDTH).fill(TileType.Grass));
    this.entities = Array(GRID_HEIGHT).fill(0).map(() => Array(GRID_WIDTH).fill(null));
    this.tileActors = Array(GRID_HEIGHT).fill(0).map(() => Array(GRID_WIDTH).fill(null));
  }

  // Initialize the grid with terrain tiles
  public initialize(pattern?: TileType[][]) {
    if (pattern) {
      this.tiles = pattern;
    } else {
      // Simple default pattern with a mix of tile types
      this.generateDefaultPattern();
    }
    this.createTiles();
  }

  // Create visual representations of tiles
  private createTiles() {
    // Clear existing tile actors first
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        if (this.tileActors[y][x]) {
          this.game.remove(this.tileActors[y][x]);
          this.tileActors[y][x] = null;
        }
      }
    }

    // Create new tiles based on the current pattern
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        const tileType = this.tiles[y][x];
        const tileProps = TILE_PROPERTIES[tileType];
        
        const tile = new ex.Actor({
          pos: new ex.Vector(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2),
          width: TILE_SIZE,
          height: TILE_SIZE,
          color: tileProps.color,
          collisionType: ex.CollisionType.PreventCollision // Disable collision detection
        });
        
        this.game.add(tile);
        this.tileActors[y][x] = tile;
      }
    }
  }

  // Check if a move is valid (within bounds and walkable tile)
  public isValidMove(x: number, y: number, animalType?: string): boolean {
    // Check bounds
    if (x < 0 || x >= GRID_WIDTH || y < 0 || y >= GRID_HEIGHT) {
      return false;
    }

    // Get the tile type at the target position
    const tileType = this.tiles[y][x];
    
    // Special case for fish - they can only move in water tiles
    if (animalType === 'fish') {
      // Fish can only move into water tiles
      if (tileType !== TileType.Water) {
        return false;
      }
    } else {
      // For non-fish animals, check if the tile is walkable according to properties
      // AND make sure it's not water (land animals can't go in water)
      if (!TILE_PROPERTIES[tileType].walkable || tileType === TileType.Water) {
        return false;
      }
    }

    // Check if the tile is already occupied
    if (this.entities[y][x] !== null) {
      return false;
    }

    return true;
  }

  // Get the tile type at a specific position
  public getTileAt(x: number, y: number): TileType | null {
    if (x < 0 || x >= GRID_WIDTH || y < 0 || y >= GRID_HEIGHT) {
      return null;
    }
    return this.tiles[y][x];
  }

  // Update entity position in the grid
  public moveEntity(entity: Entity, fromX: number, fromY: number, toX: number, toY: number) {
    // Clear old position
    if (fromX >= 0 && fromY >= 0 && fromX < GRID_WIDTH && fromY < GRID_HEIGHT) {
      this.entities[fromY][fromX] = null;
    }

    // Set new position
    if (toX >= 0 && toY >= 0 && toX < GRID_WIDTH && toY < GRID_HEIGHT) {
      this.entities[toY][toX] = entity;
    }
  }

  // Add entity to the grid
  public addEntity(entity: Entity, x: number, y: number) {
    if (x >= 0 && y >= 0 && x < GRID_WIDTH && y < GRID_HEIGHT) {
      this.entities[y][x] = entity;
    }
  }

  // Remove entity from the grid
  public removeEntity(entity: Entity) {
    const { gridX, gridY } = entity;
    if (gridX >= 0 && gridY >= 0 && gridX < GRID_WIDTH && gridY < GRID_HEIGHT) {
      this.entities[gridY][gridX] = null;
    }
  }

  // Get entity at specific position
  public getEntityAt(x: number, y: number): Entity | null {
    if (x < 0 || x >= GRID_WIDTH || y < 0 || y >= GRID_HEIGHT) {
      return null;
    }
    return this.entities[y][x];
  }

  // Get all entities within a certain range (Manhattan distance)
  public getEntitiesInRange(centerX: number, centerY: number, range: number): Entity[] {
    const entities: Entity[] = [];

    for (let y = Math.max(0, centerY - range); y <= Math.min(GRID_HEIGHT - 1, centerY + range); y++) {
      for (let x = Math.max(0, centerX - range); x <= Math.min(GRID_WIDTH - 1, centerX + range); x++) {
        const distance = Math.abs(x - centerX) + Math.abs(y - centerY);
        if (distance <= range && this.entities[y][x] !== null) {
          entities.push(this.entities[y][x]!);
        }
      }
    }

    return entities;
  }

  // Get all cells within a certain range (used for decision-making visualization)
  public getCellsInRange(centerX: number, centerY: number, range: number): GridPosition[] {
    const cells: GridPosition[] = [];
    
    // Reset the position pool before reusing it
    this.positionPool.resetPool();

    for (let y = Math.max(0, centerY - range); y <= Math.min(GRID_HEIGHT - 1, centerY + range); y++) {
      for (let x = Math.max(0, centerX - range); x <= Math.min(GRID_WIDTH - 1, centerX + range); x++) {
        const distance = Math.abs(x - centerX) + Math.abs(y - centerY);
        if (distance <= range) {
          // Get position from pool instead of creating a new object
          cells.push(this.positionPool.get(x, y));
        }
      }
    }

    return cells;
  }

  // Generate a default pattern for the grid with a mix of terrain types
  private generateDefaultPattern(): void {
    // Fill the grid with grass
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        this.tiles[y][x] = TileType.Grass;
      }
    }

    // Add a river (water) winding through the map
    let riverY = Math.floor(GRID_HEIGHT / 2);
    let riverX = 0;
    
    while (riverX < GRID_WIDTH) {
      this.tiles[riverY][riverX] = TileType.Water;
      
      // Sometimes add width to the river
      if (Math.random() < 0.5 && riverY > 1) {
        this.tiles[riverY - 1][riverX] = TileType.Water;
      }
      if (Math.random() < 0.5 && riverY < GRID_HEIGHT - 2) {
        this.tiles[riverY + 1][riverX] = TileType.Water;
      }
      
      // Make the river wind slightly
      const direction = Math.random();
      if (direction < 0.3 && riverY > 2) {
        riverY--;
      } else if (direction < 0.6 && riverY < GRID_HEIGHT - 3) {
        riverY++;
      }
      
      riverX++;
    }
    
    // Add forest clusters
    for (let i = 0; i < 5; i++) {
      const forestX = Math.floor(Math.random() * GRID_WIDTH);
      const forestY = Math.floor(Math.random() * GRID_HEIGHT);
      const forestSize = Math.floor(Math.random() * 5) + 3;
      
      this.createForestCluster(forestX, forestY, forestSize);
    }
    
    // Add mountain ranges
    for (let i = 0; i < 3; i++) {
      const mtnX = Math.floor(Math.random() * GRID_WIDTH);
      const mtnY = Math.floor(Math.random() * GRID_HEIGHT);
      const mtnSize = Math.floor(Math.random() * 4) + 2;
      
      this.createMountainCluster(mtnX, mtnY, mtnSize);
    }
    
    // Add some paths
    this.createPaths();
  }
  
  // Helper to create a forest cluster
  private createForestCluster(startX: number, startY: number, size: number): void {
    for (let y = Math.max(0, startY - size); y <= Math.min(GRID_HEIGHT - 1, startY + size); y++) {
      for (let x = Math.max(0, startX - size); x <= Math.min(GRID_WIDTH - 1, startX + size); x++) {
        const distance = Math.sqrt(Math.pow(x - startX, 2) + Math.pow(y - startY, 2));
        if (distance <= size && Math.random() < 0.6 && this.tiles[y][x] === TileType.Grass) {
          this.tiles[y][x] = TileType.Forest;
        }
      }
    }
  }
  
  // Helper to create a mountain cluster
  private createMountainCluster(startX: number, startY: number, size: number): void {
    for (let y = Math.max(0, startY - size); y <= Math.min(GRID_HEIGHT - 1, startY + size); y++) {
      for (let x = Math.max(0, startX - size); x <= Math.min(GRID_WIDTH - 1, startX + size); x++) {
        const distance = Math.sqrt(Math.pow(x - startX, 2) + Math.pow(y - startY, 2));
        if (distance <= size && Math.random() < 0.7 && this.tiles[y][x] === TileType.Grass) {
          this.tiles[y][x] = TileType.Mountain;
        }
      }
    }
  }
  
  // Helper to create paths
  private createPaths(): void {
    // Create a horizontal path
    const pathY = Math.floor(Math.random() * (GRID_HEIGHT - 5)) + 2;
    for (let x = 0; x < GRID_WIDTH; x++) {
      if (this.tiles[pathY][x] !== TileType.Water) {
        this.tiles[pathY][x] = TileType.Path;
      }
    }
    
    // Create a vertical path
    const pathX = Math.floor(Math.random() * (GRID_WIDTH - 5)) + 2;
    for (let y = 0; y < GRID_HEIGHT; y++) {
      if (this.tiles[y][pathX] !== TileType.Water) {
        this.tiles[y][pathX] = TileType.Path;
      }
    }
  }

  // Draw grid lines for debugging
  public drawDebugGrid() {
    // Draw horizontal grid lines
    for (let y = 0; y <= GRID_HEIGHT; y++) {
      const line = new ex.Actor({
        pos: new ex.Vector(GRID_WIDTH * TILE_SIZE / 2, y * TILE_SIZE),
        width: GRID_WIDTH * TILE_SIZE,
        height: 1,
        color: ex.Color.fromHex('#333333'), // Darker, less intrusive grid lines
        collisionType: ex.CollisionType.PreventCollision // Disable collision detection
      });
      this.game.add(line);
    }

    // Draw vertical grid lines
    for (let x = 0; x <= GRID_WIDTH; x++) {
      const line = new ex.Actor({
        pos: new ex.Vector(x * TILE_SIZE, GRID_HEIGHT * TILE_SIZE / 2),
        width: 1,
        height: GRID_HEIGHT * TILE_SIZE,
        color: ex.Color.fromHex('#333333'), // Darker, less intrusive grid lines
        collisionType: ex.CollisionType.PreventCollision // Disable collision detection
      });
      this.game.add(line);
    }
  }
  
  // Get all entities
  public getAllEntities(): Entity[] {
    const entities: Entity[] = [];
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        if (this.entities[y][x] !== null) {
          entities.push(this.entities[y][x]!);
        }
      }
    }
    return entities;
  }
  
  // Get grid dimensions
  public getWidth(): number {
    return GRID_WIDTH;
  }
  
  public getHeight(): number {
    return GRID_HEIGHT;
  }
  
  // Get a cached vector to avoid creating new Vector objects
  public getCachedVector(x: number, y: number): ex.Vector {
    this.cachedVector.x = x;
    this.cachedVector.y = y;
    return this.cachedVector;
  }

  // Clear all entities from the grid
  public clearEntities() {
    this.entities = Array(GRID_HEIGHT).fill(0).map(() => Array(GRID_WIDTH).fill(null));
  }

  // Set a specific tile type
  public setTile(x: number, y: number, type: TileType) {
    if (x >= 0 && x < GRID_WIDTH && y >= 0 && y < GRID_HEIGHT) {
      this.tiles[y][x] = type;
      
      // Update the visual representation
      if (this.tileActors[y][x]) {
        this.game.remove(this.tileActors[y][x]);
      }
      
      const tileProps = TILE_PROPERTIES[type];
      const tile = new ex.Actor({
        pos: new ex.Vector(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2),
        width: TILE_SIZE,
        height: TILE_SIZE,
        color: tileProps.color,
        collisionType: ex.CollisionType.PreventCollision // Disable collision detection
      });
      
      this.game.add(tile);
      this.tileActors[y][x] = tile;
    }
  }
}